import Invoice from '../../common/models/Invoice.ts';
import Driver from '../types/Driver.ts';
import PdfPageWithText from '../types/PdfPageWithText.ts';
import PdfProcessedPage from '../types/PdfProcessedPage.ts';
import PdfTextTest, { StringAtIndexTest } from '../types/PdfTextTest.ts';
import { compute_pdf_line_items, compute_pdf_tests } from '../pdf_parser.ts';
import { stamp } from '../pdf_writer.ts';
import PdfLineItem from '../types/PdfLineItem.ts';

export default class extends Driver {
    constructor() {
        super()
    }

    parse_project_NaN(pdf_text_contents: string[]): string {   
        const tests = [
            {
                "string": "PO",
                "elements_to_skip": 2,
            } as StringAtIndexTest,
            {
                "string": "#",
                "elements_to_skip": 4,
            } as StringAtIndexTest,
        ] as StringAtIndexTest[]
             
        let pdf_tests = {
            "first_string": 'Customer',
            "tests": tests,
            "elements_to_skip_to_return": 6
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }
    
    parse_invoice_NaN(pdf_text_contents: string[]): string {
        const tests = [
            {
                "string": "INVOICE",
                "elements_to_skip": 2,
            } as StringAtIndexTest,
            {
                "string": "NUMBER",
                "elements_to_skip": 4,
            } as StringAtIndexTest,
            {
                "string": "Invoice",
                "elements_to_skip": 5,
            } as StringAtIndexTest,
        ] as StringAtIndexTest[]

        let pdf_tests = {
            "first_string": 'INVOICE',
            "tests": tests,
            "elements_to_skip_to_return": 12
        } as PdfTextTest
    
        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }

    parse_invoice_line_items(pdf_text_contents: string[]): PdfLineItem[] | null {
        const tests = [
            {
                "string": "SHIP VIA",
                "elements_to_skip": 2,
            }  as StringAtIndexTest,
            {
                "string": "SHIPPED",
                "elements_to_skip": 4,
            } as StringAtIndexTest,
        ]

        let pdf_tests = {
            "first_string": 'SHIP POINT',
            "tests": tests,
            "elements_to_skip_to_return": 16
        } as PdfTextTest

        const line_item_parser = function (text_contents: string[], starting_index: number) {
            let line_items = []
            let current_index_cursor = starting_index
            let current_line_number = ""
            let next_line_number = ""
    
            do {
                current_line_number = text_contents[current_index_cursor]
                next_line_number = "" + (parseInt(current_line_number) + 1)
                
                let new_line_item = {
                    "line_number": text_contents[current_index_cursor],
                    "part_name": text_contents[current_index_cursor + 2],
                    "quantity": parseFloat(text_contents[current_index_cursor + 4]),
                    "quantity_back_ordered": parseFloat(text_contents[current_index_cursor + 6]),
                    "quantity_shipped": parseFloat(text_contents[current_index_cursor + 8]),
                    "unit_of_measure": text_contents[current_index_cursor + 10],
                    "price_per_unit": parseFloat(text_contents[current_index_cursor + 12]),
                    "line_item_total": parseFloat(text_contents[current_index_cursor + 14]),
                    "part_description": text_contents[current_index_cursor + 15]
                } as PdfLineItem

                current_index_cursor += 16
    
                while (
                    current_index_cursor < text_contents.length
                    && text_contents[current_index_cursor + 4] != "Continued"
                    && text_contents[current_index_cursor + 3] != "Lines Total"
                    && !(
                        text_contents[current_index_cursor] == next_line_number
                        && text_contents[current_index_cursor + 1] == " "
                    )
                )  {
                    new_line_item.part_description += "\n" + text_contents[current_index_cursor]
                    current_index_cursor++
                }
    
                line_items.push(new_line_item)
    
            } while (text_contents[current_index_cursor] == next_line_number)

            return line_items
        }
    
        return compute_pdf_line_items(pdf_text_contents, pdf_tests, line_item_parser) ?? null
    }
    
    parse_total(pdf_text_contents: string[]): number | null {
        const tests = [
            {
                "string": "Due",
                "elements_to_skip": 2,
            }  as StringAtIndexTest,
        ] as StringAtIndexTest[]

        let pdf_tests = {
            "first_string": 'Total',
            "tests": tests,
            "elements_to_skip_to_return": 8
        } as PdfTextTest
    
        const total = compute_pdf_tests(pdf_text_contents, pdf_tests)
        return this.parse_money_string(total) ?? null
    }

    check(pdf_text_contents: string[]): boolean {
        const tests = [] as StringAtIndexTest[]
  
        let pdf_tests = {
            "first_string": "www.altorfer.com",
            "tests": tests,
            "elements_to_skip_to_return": 1
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) === ""
    }

    parse_pages(pages: PdfPageWithText[]): PdfProcessedPage[] {
        return pages.map((page: PdfPageWithText) => {
            return new PdfProcessedPage({
                page_number: page.page_number,
                vendor: 'Altorfer',
                line_items: [],
                // line_items: this.parse_invoice_line_items(page.text_contents),
                project_NaN: this.parse_project_NaN(page.text_contents),
                invoice_NaN: this.parse_invoice_NaN(page.text_contents),
                total: this.parse_total(page.text_contents)
            })
        })
    }

    async stamp_invoices(invoices: Invoice[]): Promise<Invoice[]> {
        const x_coordinate = 160
        const y_coordinate = 800

        for (const index in invoices) {
            const invoice = invoices[index]
            if (invoice.pdf_bytes === null) {
                continue
            }
            
            const stamped_pdf_bytes = await stamp(x_coordinate, y_coordinate, invoice.invoice_information, invoice.pdf_bytes)
            invoices[index].stamped_pdf_bytes = stamped_pdf_bytes
            invoices[index].stamp_x = x_coordinate
            invoices[index].stamp_y = y_coordinate
        }

        return invoices  
    }
}
